import React, { useState } from 'react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import { useHistory, useParams } from 'react-router-dom';

import {
    createWallboard,
    updateWallboard,
    deleteWallboard,
    regenerateWallboard,
} from 'flow/actions/wallboard-actions';
import { userHasQueueWithOmniEnabled } from 'flow/selectors/selectors';

import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import {
    getWallboardUrl,
    MAX_QUEUES_AMOUNT_WALLBOARD,
    MAX_AGENTS_AMOUNT_WALLBOARD,
    wallboardTypes,
    wallboardDataIsSufficientForSaving,
    generateWallboardDTOFromComponentFriendlyData,
    getWallboardCreateComponentFriendlyWallboardData,
} from 'flow/utils/wallboard-utils';
import { routes } from 'flow/types/routes';
import { Dashboard } from 'flow/types/Dashboard';
import { DashboardKey, ExtensionKey } from 'flow/types/EntityKeys';

import { selectUrl } from 'flow/selectors/DashboardsSelectors';
import { navigateBack } from 'flow/actions/navigate-actions';
import Loading from 'flow/components/ui-components/Loading';
import Page from 'flow/components/Setting/Page';
import DashboardHuntGroupsMultiple from 'flow/components/dashboard/DashboardHuntGroupsMultiple';
import DashboardTeamCustom from 'flow/components/dashboard/DashboardTeamCustom';
import DashboardSettingsCustomize from 'flow/components/dashboard/DashboardSettingsCustomize';
import DashboardSettingsVisibility from 'flow/components/dashboard/DashboardSettingsVisibility';
import DashboardButtonsModify from './DashboardButtonsModify';
import { TypedMap } from 'flow/types/TypedMap';
import { generatePath } from 'flow/utils/router-utils';

interface Props {
    boardType: 'hunt_groups_multiple' | 'team_custom';
    dashboardFromStore?: TypedMap<Dashboard>;
    dashboardKey?: DashboardKey;
}
const DashboardCreate = ({
    dashboardKey,
    boardType,
    dashboardFromStore,
}: Props) => {
    const { i18next } = useAppContext();
    const dispatch: any = useAppDispatch();
    const history = useHistory();
    const { openModal } = useModal();

    const { extensionKey } = useParams<{ extensionKey: ExtensionKey }>();

    const dashboardUrl = useAppSelector((state) =>
        selectUrl(state, dashboardKey),
    );

    const userHasOmniQueue = useAppSelector(userHasQueueWithOmniEnabled);

    const isEditingExistingDashboard = !!dashboardKey;

    const [dashboardDataForSaving, setDashboardDataForSaving] = useState(
        getWallboardCreateComponentFriendlyWallboardData(
            dashboardFromStore,
            boardType,
        ),
    );

    const [isLoading, setIsLoading] = useState(false);
    const [view, setView] = useState('MAIN');
    const [generatingNewWallboardUrl, setGeneratingNewWallboardUrl] =
        useState(false);
    const [newWallboardUrlGenerated, setNewWallboardUrlGenerated] =
        useState(false);

    const handleWallboardDataChanged = (data: any) => {
        setDashboardDataForSaving(data);
    };

    const handleSaveWallboard = () => {
        if (!wallboardDataIsSufficientForSaving(dashboardDataForSaving)) {
            //Should never happen because the create/save button should be disabled if data is insufficient for saving
            return;
        }

        setIsLoading(true);
        const wallboardDTO = generateWallboardDTOFromComponentFriendlyData(
            dashboardDataForSaving,
        );

        if (isEditingExistingDashboard) {
            const wallboardDTOWithKey = wallboardDTO.set('key', dashboardKey);
            dispatch(updateWallboard(wallboardDTOWithKey, dashboardKey))
                .then(() => {
                    setIsLoading(false);
                    handleBack();
                })
                .catch(() => {
                    setIsLoading(false);
                });
        } else {
            dispatch(createWallboard(wallboardDTO))
                .then((data: Dashboard) => {
                    setIsLoading(false);
                    openModal('dashboardCreated', {
                        url: getWallboardUrl(data['urlPath']),
                    });
                })
                .catch(() => {
                    setIsLoading(false);
                });
        }
    };

    const handleGenerateNewWallboardUrl = () => {
        setGeneratingNewWallboardUrl(true);
        dispatch(regenerateWallboard(dashboardKey))
            .then(() => {
                setNewWallboardUrlGenerated(true);
            })
            .finally(() => {
                setGeneratingNewWallboardUrl(false);
            });
    };

    const handleBack = () => {
        if (view === 'SETTINGS' || view === 'VISIBILITY') {
            setView('MAIN');
        } else {
            dispatch(navigateBack(location));
        }
    };

    const handleDeleteWallboard = () => {
        setIsLoading(true);
        dispatch(deleteWallboard(dashboardKey)).finally(() => {
            history.push(
                generatePath(routes['settings.dashboards'], {
                    extensionKey,
                }),
            );
        });
    };

    const navigateToSettings = () => {
        setView('SETTINGS');
    };

    const navigateToWallboardVisibilitySettings = () => {
        setView('VISIBILITY');
    };

    const getCreateEditDeleteButtons = () => {
        return (
            <div className="end-0 px-3 py-1.5">
                <DashboardButtonsModify
                    onClickSave={handleSaveWallboard}
                    createIsAllowed={wallboardDataIsSufficientForSaving(
                        dashboardDataForSaving,
                    )}
                    isEditingExistingDashboard={isEditingExistingDashboard}
                    onClickDelete={handleDeleteWallboard}
                />
            </div>
        );
    };

    const onWallboardSettingSet = (settingName: string, value: number) => {
        const newData = dashboardDataForSaving.setIn(
            ['settings', settingName],
            value,
        );
        setDashboardDataForSaving(newData);
    };

    const getWallboardItem = (
        boardType: 'hunt_groups_multiple' | 'team_custom',
    ) => {
        if (view === 'SETTINGS') {
            const props = {
                dataPeriodTypeInit: dashboardDataForSaving.getIn([
                    'settings',
                    'dataPeriodType',
                ]),
                dataPeriodInit: parseInt(
                    dashboardDataForSaving.getIn(['settings', 'dataPeriod']),
                ),
                serviceLevelPeriodInit: parseInt(
                    dashboardDataForSaving.getIn([
                        'settings',
                        'serviceLevelPeriod',
                    ]),
                ),
                serviceLevelWarningThresholdInit: parseInt(
                    dashboardDataForSaving.getIn([
                        'settings',
                        'serviceLevelWarningThreshold',
                    ]),
                ),
                onSettingSet: onWallboardSettingSet,
                boardType,
                userHasOmniQueue,
            };

            return <DashboardSettingsCustomize {...props} />;
        } else if (view === 'VISIBILITY') {
            const props = {
                dashboardUrl: dashboardUrl,
                displayLoadingNewUrl: generatingNewWallboardUrl,
                displayGenerationSuccessful: newWallboardUrlGenerated,
                onGenerateNewUrlClick: handleGenerateNewWallboardUrl,
            };

            return <DashboardSettingsVisibility {...props} />;
        } else {
            const navigateToVisibilitySettings = isEditingExistingDashboard
                ? navigateToWallboardVisibilitySettings
                : undefined;

            const props = {
                wallboardData: dashboardDataForSaving,
                maxSelectionAmount: MAX_QUEUES_AMOUNT_WALLBOARD,
                onWallboardDataChanged: handleWallboardDataChanged,
                onNavigateToCustomizeSettings: navigateToSettings,
                onNavigateToVisibilitySettings: navigateToVisibilitySettings,
                userHasOmniQueue,
            };

            switch (boardType) {
                case wallboardTypes.HUNT_GROUPS_MULTIPLE:
                    return <DashboardHuntGroupsMultiple {...props} />;
                case wallboardTypes.TEAM_CUSTOM:
                    props.maxSelectionAmount = MAX_AGENTS_AMOUNT_WALLBOARD;
                    return <DashboardTeamCustom {...props} />;
                default:
                    return null;
            }
        }
    };

    const getViewTitle = () => {
        if (view === 'VISIBILITY') {
            return i18next.t('wallboard.sharing');
        } else if (isEditingExistingDashboard) {
            return i18next.t('wallboard.update');
        } else {
            return i18next.t('wallboard.create_new');
        }
    };

    if (isLoading || (isEditingExistingDashboard && !dashboardFromStore)) {
        return <Loading />;
    }

    return (
        <Page
            header={getViewTitle()}
            onClickBack={handleBack}
            footer={view === 'MAIN' ? getCreateEditDeleteButtons() : null}
        >
            {getWallboardItem(dashboardDataForSaving.get('type'))}
        </Page>
    );
};

export default DashboardCreate;
